/* =============================================================================
   lp-modal.js — locked LP upsells modal engine (LP framework).
   -----------------------------------------------------------------------------
   Public API (extends window.BBELP):
     BBELP.modal.init(config) -> { open, close, reset }
     BBELP.modal.open()        -> opens last initialized instance

   config:
     root                  — section root [data-st11-root]
     getParentVariantIds   — fn -> number[] (BOGO / parent selections)
     getParentProductIds   — fn -> number[] (BOGO parent product ids; tanner dedupe)
     getParentCartItems    — fn -> { id, quantity }[] for /cart/add.js
     canCheckout           — fn(opts) -> boolean (parent gate)
     onCheckoutBlocked     — fn(opts) optional when canCheckout false
     onStickySuppress      — fn(on) optional; defaults to BBELP.sticky.suppress

   Tanner dedupe (on modal open, upsell step only):
     When cart OR parent BOGO selection includes any tanner product, hide ALL
     upsell cards whose product is a tanner (data-is-tanner or handle/type heuristics).
     Accessories (brush/kabuki/mitt/glove/drops) are never treated as tanners.
     Exact variant dedupe still applies for all modal cards (cart + parent variants).

   Modal element: [data-lp-upsells-root] or [data-st11-modal] inside root.
   Config JSON: data-lp-upsells-config on modal element.

   Selection:
     Upsell-only flow (enable_upsell_step on, enable_free_gift off) is MULTI-pick
     — every selected card is added as its own line. With the gift step on, step 1
     is a gate that reads exactly one selection (it advances the step, names the
     add-on in the secured banner, and clears the gift on deselect), so that flow
     stays single-pick. NOTHING is pre-selected in either flow — under multi-pick
     a default selection is added alongside the next click rather than swapped
     out, so it would bill an add-on the shopper never chose. Selected cards
     render in the page's accent, carried onto
     the portaled modal as an inline `--lp-modal-select-accent`; a page whose
     accent is too pale for the white button label sets that token itself.

   Analytics: self-contained (no cart-analytics.js edits).
   Line properties + dataLayer bbe_upsell_add; recordCartStat best-effort.

   Unified Offer Contract: upsell/gift adds dual-write signed `_bbe_offer`
   (type lp/gift, premint on modal open via app proxy api/offer/mint) plus
   `_upsell_qty` on paid upsells, alongside legacy `_is_lp_upsells*` props.

   Discounts:
     - Parent BOGO code: liquidAjaxCart.update / cart/update.js after /cart/add.js,
       then redirect to /checkout (never nested /discount/ URLs).
     - Paid upsell: THIS IS A POPUP and prices on the PPUP surface, same as the
       cart's pre-purchase popup. The line carries `_is_pre_purchase_popup_upsell`
       to select ppup mode in `upsell-price-transform`; authorization is the DEAL
       collection or `bbe_ppup.hi_ltv`, neither forgeable. The card advertises the
       offer only when `bbe_offers.ppup_eligible_product_ids` (the mirror of that
       union) plus the shared tag and selling-plan guards all allow it, so an
       ineligible product renders — and is charged — full price. Never add the LTO
       marker: `claimedMode` returns null for a line claiming both surfaces.
     - Analytics ride the PPUP surface too (`__cartPreupsell`, track source
       `pre_purchase_popup_upsell`) so all popup analytics aggregate together;
       `_is_lp_upsells` remains on the line to split LP back out when needed.
     - Gift (`_is_lp_upsells_gift`): NOT PRICED. A cart transform cannot make a
       line free (discountedUnitPrice rejects >= 100%), and line-property-discount's
       gift branch keys on `_smgift`, which this file does not write. Keep
       `enable_free_gift` off until the gift routes through `_smgift` with the
       `free-gift` / `checkout-auto-freebie` Admin tags isGiftEligible requires.
     - installLpUpsellsCartSync(): removes gift lines when upsell lines leave cart.
============================================================================= */
(function () {
  "use strict";

  window.BBELP = window.BBELP || {};

  var lastInstance = null;
  var lpUpsellsSyncInstalled = false;
  var lpUpsellsSyncBusy = false;

  function isLpUpsellLine(item) {
    if (!item || !item.properties) return false;
    var v = item.properties._is_lp_upsells;
    return v === "true" || v === true;
  }

  function isLpUpsellGiftLine(item) {
    if (!item || !item.properties) return false;
    var v = item.properties._is_lp_upsells_gift;
    return v === "true" || v === true;
  }

  function removeLpUpsellGiftLines(giftLines) {
    if (!giftLines || !giftLines.length) return Promise.resolve();

    if (window.liquidAjaxCart && typeof window.liquidAjaxCart.change === "function") {
      return new Promise(function (resolve) {
        var i = 0;
        function next() {
          if (i >= giftLines.length) {
            resolve();
            return;
          }
          var item = giftLines[i++];
          var lineKey = item.key != null ? String(item.key).trim() : "";
          if (!lineKey) {
            next();
            return;
          }
          window.liquidAjaxCart.change(
            { id: lineKey, quantity: 0 },
            { info: { lp_upsells_sync: true }, lastCallback: next }
          );
        }
        next();
      });
    }

    return giftLines.reduce(function (chain, item) {
      return chain.then(function () {
        var lineKey = item.key != null ? String(item.key).trim() : "";
        if (!lineKey) return Promise.resolve();
        return fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: lineKey, quantity: 0 }),
        }).catch(function () {});
      });
    }, Promise.resolve());
  }

  function syncLpUpsellsGiftWithCart(cart) {
    if (!cart || !Array.isArray(cart.items) || lpUpsellsSyncBusy) return Promise.resolve();

    var hasUpsell = false;
    var giftLines = [];

    cart.items.forEach(function (item) {
      if (isLpUpsellLine(item)) hasUpsell = true;
      if (isLpUpsellGiftLine(item)) giftLines.push(item);
    });

    if (hasUpsell || !giftLines.length) return Promise.resolve();

    lpUpsellsSyncBusy = true;
    return removeLpUpsellGiftLines(giftLines).finally(function () {
      lpUpsellsSyncBusy = false;
    });
  }

  function installLpUpsellsCartSync() {
    if (lpUpsellsSyncInstalled) return;
    lpUpsellsSyncInstalled = true;

    document.addEventListener("liquid-ajax-cart:request-end", function (ev) {
      var detail = ev && ev.detail;
      var requestState = detail && detail.requestState;
      var cart = detail && detail.cart;

      if (!cart || !requestState || !requestState.responseData || requestState.responseData.ok === false) {
        return;
      }
      if (requestState.info && requestState.info.lp_upsells_sync) return;
      if (requestState.requestType === "add") return;

      syncLpUpsellsGiftWithCart(cart);
    });
  }

  installLpUpsellsCartSync();

  /** LP modal gift (`_is_lp_upsells_gift`) + PPUP free row (`_is_pre_upsell`). */
  function isLpPpUpsellFreeLine(item) {
    if (!item || !item.properties) return false;
    var p = item.properties;
    var gift = p._is_lp_upsells_gift;
    if (gift === "true" || gift === true) return true;
    var preUpsell = p._is_pre_upsell;
    return preUpsell === "true" || preUpsell === true;
  }

  function countLpPpUpsellFreeLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    var total = 0;
    cart.items.forEach(function (item) {
      if (!isLpPpUpsellFreeLine(item)) return;
      var qty = Number(item.quantity);
      total += Number.isFinite(qty) && qty > 0 ? qty : 1;
    });
    return total;
  }

  function getCartSnapshot() {
    return window.liquidAjaxCart && window.liquidAjaxCart.cart
      ? window.liquidAjaxCart.cart
      : null;
  }

  window.bbeCountLpPpUpsellFreeLines = countLpPpUpsellFreeLines;

  /* ── Unified Offer Contract (`_bbe_offer`) premint — mirrors pre-upsell.js.
     Fire-and-forget on modal open / variant change; ATC never blocks on it.
     If no cached token by click, lines carry legacy props only. ── */
  var OFFER_MINT_URL = "/apps/bbe-checkout-app/api/offer/mint";
  var OFFER_MINT_STORAGE_PREFIX = "bbe_offer_mint:";

  function offerMintDayKey() {
    var d = new Date();
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
      .toISOString()
      .slice(0, 10);
  }

  function offerMintCacheKey(offerId, variantId, aq) {
    return (
      OFFER_MINT_STORAGE_PREFIX +
      offerId + ":" + variantId + "::" + (aq || 1) + ":" + offerMintDayKey()
    );
  }

  function readOfferMintCache(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeOfferMintCache(key, token) {
    try {
      sessionStorage.setItem(key, token);
    } catch (e) {
      /* quota / private mode */
    }
  }

  function lpOfferIdForCard(mode, d) {
    var pid = d.id || d.variant || "x";
    return (mode === "gift" ? "gift-" : "lp-") + pid;
  }

  /** offers: [{ mode, id, variant, pct }] — type lp (upsell) / gift. */
  function premintLpOffers(offers) {
    if (!Array.isArray(offers) || !offers.length) return;
    var bodyOffers = [];
    offers.forEach(function (o) {
      if (!o || !o.variant) return;
      var offerId = lpOfferIdForCard(o.mode, o);
      var key = offerMintCacheKey(offerId, o.variant, 1);
      if (readOfferMintCache(key)) return;
      bodyOffers.push({
        offerId: offerId,
        variantId: String(o.variant),
        aq: 1,
        type: o.mode === "gift" ? "gift" : "lp",
        pct: o.mode === "gift" ? 100 : o.pct || null,
        _key: key,
      });
    });
    if (!bodyOffers.length) return;
    fetch(OFFER_MINT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        offers: bodyOffers.map(function (o) {
          return {
            offerId: o.offerId,
            variantId: o.variantId,
            aq: o.aq,
            type: o.type,
            pct: o.pct,
          };
        }),
      }),
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        var results = data && data.results;
        if (!Array.isArray(results)) return;
        results.forEach(function (r, i) {
          if (r && r.ok && r.token && bodyOffers[i]) {
            writeOfferMintCache(bodyOffers[i]._key, r.token);
          }
        });
      })
      .catch(function () {});
  }

  function setLoading(el, on) {
    if (window.BBELP && typeof window.BBELP.setLoading === "function") {
      window.BBELP.setLoading(el, on);
    } else if (el && el.classList) {
      el.classList.toggle("is-loading", on !== false);
      if (on !== false) {
        el.setAttribute("aria-busy", "true");
      } else {
        el.removeAttribute("aria-busy");
      }
    }
  }

  function money(cents) {
    var n = (Number(cents) || 0) / 100;
    return "$" + n.toFixed(2);
  }

  function cardData(card) {
    return {
      el: card,
      id: card.getAttribute("data-st11-id"),
      variant: card.getAttribute("data-st11-variant"),
      price: parseInt(card.getAttribute("data-st11-price"), 10) || 0,
      title: card.getAttribute("data-st11-title") || "",
      img: card.getAttribute("data-st11-img") || "",
      available: card.getAttribute("data-st11-available") === "true",
      slot: card.getAttribute("data-lp-upsells-slot") || "",
      mode: card.getAttribute("data-st11-mode") || "",
    };
  }

  function trackProps(mode, slot) {
    var props = {
      __track_source: "lp_upsells",
      __track_event: "add",
      __track_slot: String(slot || ""),
    };
    if (mode === "gift") {
      props._is_lp_upsells_gift = "true";
    } else {
      // `_is_lp_upsells` stays as the theme's own plumbing (qty lock, gift
      // cart-sync, BOGO exclusion) and as the sub-surface discriminator inside
      // the popup bucket, so LP can still be split out of PPUP when wanted.
      props._is_lp_upsells = "true";

      // Money marker. `upsell-price-transform` prices a ppup-mode line at
      // `transform_config.percent.ppup`, authorized by the DEAL collection or
      // `bbe_ppup.hi_ltv` — signals the shopper cannot forge, so the marker
      // alone never moves money. Written unconditionally: display is what gates
      // on eligibility, so a stale mirror can only under-promise (charged less
      // than shown), never over-charge. Never write the LTO marker alongside
      // this one — `claimedMode` returns null for a line claiming both.
      props._is_pre_purchase_popup_upsell = "true";

      // Analytics canonical, matching `buildDynamicLineProperties` in
      // pre-upsell.js so popup adds aggregate on one surface.
      props._is_prepurchase_popup_upsell = "true";
      props.__cartPreupsell = "true";
      props.__track_source = "pre_purchase_popup_upsell";
      props.__track_event = "prePurchasePopupUpsellAdd";
    }
    return props;
  }

  function init(config) {
    config = config || {};
    var root = config.root;
    if (!root || !root.querySelector) {
      return { open: function () {}, close: function () {}, reset: function () {} };
    }

    var modal =
      root.querySelector("[data-lp-upsells-root]") ||
      root.querySelector("[data-st11-modal]");
    var rootId = root.id || "";

    if (!modal && rootId) {
      modal = document.querySelector(
        'body > [data-lp-upsells-root][data-st11-root-id="' +
          rootId +
          '"], body > [data-st11-modal][data-st11-root-id="' +
          rootId +
          '"]'
      );
    }

    if (modal && modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
    if (modal && rootId) {
      modal.setAttribute("data-st11-root-id", rootId);
      modal.dataset.lpUpsellsPortaled = "1";
    }

    // Portaling to <body> severs the modal from #section-…, which is where every
    // LP declares its palette — so any var() the cards read would silently fall
    // back to the framework default and every page's modal would look alike.
    // Carry the resolved accent across as an inline property on the modal.
    // --lp-modal-select-accent is the escape hatch for a page whose accent is too
    // pale to carry the white button label, so it is consulted first.
    if (modal && window.getComputedStyle) {
      var rootStyle = window.getComputedStyle(root);
      var accentNames = [
        "--lp-modal-select-accent",
        "--st11-select-accent",
        "--lp-card-accent",
        "--lp-color-accent",
      ];
      for (var ai = 0; ai < accentNames.length; ai++) {
        var accent = (rootStyle.getPropertyValue(accentNames[ai]) || "").trim();
        // An engine that hands back an unsubstituted var() would resolve to
        // nothing against <body>; drop it and let the stylesheet default win.
        if (!accent || accent.indexOf("var(") !== -1) continue;
        modal.style.setProperty("--lp-modal-select-accent", accent);
        break;
      }
    }

    var modalConfig = {};
    if (modal) {
      try {
        modalConfig = JSON.parse(modal.getAttribute("data-lp-upsells-config") || "{}");
      } catch (e) {
        modalConfig = {};
      }
    }

    var discountCode = String(modalConfig.discountCode || "").trim();
    var enableUpsellStep =
      modalConfig.enableUpsellStep !== false && String(modalConfig.enableUpsellStep) !== "false";
    var enableFreeGift =
      modalConfig.enableFreeGift !== false && String(modalConfig.enableFreeGift) !== "false";
    // Multi-pick is only coherent while the upsell step stands alone. Once the
    // gift step is live, step 1 is a gate: selecting advances to step 2, the
    // secured banner names THE add-on that unlocked the gift, and deselecting
    // clears the gift. All of that reads one selection, so that flow stays a
    // single pick and only the upsell-only flow opens up.
    var allowMultiUpsell = enableUpsellStep && !enableFreeGift;
    var cartAddUrl = modalConfig.cartAddUrl || "/cart/add.js";
    var checkoutUrl = modalConfig.checkoutUrl || "/checkout";
    // Blank badge means the shell's mirror read found no live offer — render
    // full price rather than falling back to a hardcoded 25%.
    var upsellOfferBadge = String(modalConfig.upsellOfferBadge || "").trim();
    var upsellOfferPct = parseInt(upsellOfferBadge, 10);
    if (!isFinite(upsellOfferPct) || upsellOfferPct < 1 || upsellOfferPct > 90) {
      upsellOfferPct = 0;
    }

    var getParentVariantIds =
      typeof config.getParentVariantIds === "function"
        ? config.getParentVariantIds
        : function () {
            return [];
          };
    var getParentProductIds =
      typeof config.getParentProductIds === "function"
        ? config.getParentProductIds
        : function () {
            return [];
          };
    var getParentCartItems =
      typeof config.getParentCartItems === "function"
        ? config.getParentCartItems
        : function () {
            return [];
          };
    var canCheckout =
      typeof config.canCheckout === "function"
        ? config.canCheckout
        : function () {
            return true;
          };
    var onCheckoutBlocked =
      typeof config.onCheckoutBlocked === "function" ? config.onCheckoutBlocked : null;

    function stickySuppress(on) {
      if (typeof config.onStickySuppress === "function") {
        config.onStickySuppress(on);
        return;
      }
      if (window.BBELP && window.BBELP.sticky && typeof window.BBELP.sticky.suppress === "function") {
        window.BBELP.sticky.suppress(on);
      }
    }

    var modalRoot = modal || root;
    var modalCloseBtn = modalRoot.querySelector(".st11-modal__close");
    var modalBackBtn = modalRoot.querySelector("[data-st11-modal-back]");
    var modalBackBar = modalRoot.querySelector("[data-st11-modal-back-bar]");
    var giftStep = modalRoot.querySelector("[data-st11-gift-step]");
    var step1Panel = modalRoot.querySelector('[data-st11-modal-step="1"]');
    var step2Panel = modalRoot.querySelector('[data-st11-modal-step="2"]');
    var progressLine = modalRoot.querySelector("[data-st11-progress-line]");
    var progressSteps = modalRoot.querySelectorAll("[data-st11-progress-step]");
    var progressStep1 = modalRoot.querySelector('[data-st11-progress-step="1"]');
    var progressStep2 = modalRoot.querySelector('[data-st11-progress-step="2"]');
    var progressConnector = modalRoot.querySelector(".st11-modal__progress-connector");
    var securedBanner = modalRoot.querySelector("[data-st11-secured-banner]");
    var securedName = modalRoot.querySelector("[data-st11-secured-name]");
    var securedOffer = modalRoot.querySelector(".st11-modal__secured-offer");
    var checkoutBtn = modalRoot.querySelector("[data-st11-checkout]");
    var step1Label = modalRoot.querySelector("[data-st11-step1-label]");
    var step1Done = modalRoot.querySelector("[data-st11-step1-done]");

    if (securedOffer && upsellOfferBadge) {
      securedOffer.textContent = " - " + upsellOfferBadge + " Secured";
    }

    var modalStep = 1;
    var checkoutBusy = false;
    var lastFocused = null;

    function emit(name, detail) {
      if (!root || !root.dispatchEvent) return;
      try {
        root.dispatchEvent(
          new CustomEvent("bbe:lp-upsells:" + name, {
            bubbles: true,
            detail: detail || {},
          })
        );
      } catch (e) {
        /* IE11 fallback omitted — landers target modern browsers */
      }
    }

    function modalCards(mode) {
      var sel = '[data-st11-card][data-st11-mode="' + mode + '"]';
      var scope = modal || root;
      return scope.querySelectorAll(sel);
    }

    function allModalCards() {
      var sel =
        '[data-st11-card][data-st11-mode="upsell"], [data-st11-card][data-st11-mode="gift"]';
      var scope = modal || root;
      return scope.querySelectorAll(sel);
    }

    function visibleModalCards(mode) {
      return Array.prototype.filter.call(modalCards(mode), function (card) {
        return !card.hidden && card.getAttribute("data-st11-available") === "true";
      });
    }

    function hideOosModalCards() {
      allModalCards().forEach(function (card) {
        if (card.getAttribute("data-st11-available") !== "true") {
          card.hidden = true;
        }
      });
    }

    function applyStepUi() {
      if (!enableUpsellStep) {
        if (step1Panel) {
          step1Panel.hidden = true;
          step1Panel.classList.remove("is-active");
        }
        if (progressStep1) {
          progressStep1.hidden = true;
        }
      }
      if (!enableFreeGift) {
        if (step2Panel) {
          step2Panel.hidden = true;
          step2Panel.classList.remove("is-active");
        }
        if (giftStep) {
          giftStep.hidden = true;
        }
        if (securedBanner) {
          securedBanner.hidden = true;
        }
      }
      if (progressConnector) {
        progressConnector.hidden = !enableUpsellStep && !enableFreeGift;
      }
      if (progressStep2) {
        progressStep2.hidden = !enableUpsellStep && !enableFreeGift;
      }
    }

    function reindexSlots() {
      ["upsell", "gift"].forEach(function (mode) {
        var slot = 0;
        modalCards(mode).forEach(function (card) {
          if (card.hidden || card.getAttribute("data-st11-available") !== "true") return;
          slot += 1;
          card.setAttribute("data-lp-upsells-slot", String(slot));
        });
      });
    }

    function cartVariantIds(cart) {
      var ids = [];
      (cart && cart.items ? cart.items : []).forEach(function (item) {
        var id = Number(item.variant_id);
        if (id) ids.push(id);
      });
      return ids;
    }

    function isTannerHandle(handle) {
      var h = String(handle || "").toLowerCase();
      return h.indexOf("self-tanner") !== -1 || h.indexOf("self_tanner") !== -1;
    }

    function isTannerProductType(type) {
      return String(type || "").toLowerCase().indexOf("tanner") !== -1;
    }

    // Tanner application accessories (brush, mitt, glove, drops, etc.) merely
    // carry the Self Tanner name — they are NOT tanners for dedupe purposes.
    // They are ideal add-ons for tanner buyers, so must never be hidden by the
    // tanner dedupe (otherwise Step 1 can render empty).
    var TANNER_ACCESSORY_RE = /\b(brush|kabuki|mitt|applicator|glove|sponge|buffer|blend(?:ing|er)?|remover|drops?)\b/;
    function isTannerAccessoryText(text) {
      return TANNER_ACCESSORY_RE.test(String(text || "").toLowerCase());
    }

    function isTannerCartItem(item) {
      if (!item) return false;
      var text = String(item.handle || "") + " " + String(item.product_title || item.title || "");
      if (isTannerAccessoryText(text)) return false;
      if (isTannerProductType(item.product_type)) return true;
      if (isTannerHandle(item.handle)) return true;
      return false;
    }

    function handleFromSt11Url(url) {
      var path = String(url || "").split("/products/").pop() || "";
      return path.split("?")[0].split("#")[0].toLowerCase();
    }

    function isTannerDomCard(card) {
      if (!card) return false;
      var handle = card.getAttribute("data-product-handle") || "";
      var urlHandle = handleFromSt11Url(card.getAttribute("data-st11-url"));
      var title = String(card.getAttribute("data-st11-title") || "").toLowerCase();
      // Accessory short-circuit: never treat brushes/mitts/applicators as
      // tanners even if their name/handle contains Self Tanner.
      if (isTannerAccessoryText(handle + " " + urlHandle + " " + title)) return false;
      if (card.getAttribute("data-is-tanner") === "true") return true;
      if (isTannerHandle(handle)) return true;
      if (isTannerHandle(urlHandle)) return true;
      return title.indexOf("tanner") !== -1;
    }

    function parentSelectionHasTanner() {
      var ids = getParentProductIds() || [];
      for (var i = 0; i < ids.length; i++) {
        var pid = Number(ids[i]);
        if (!pid) continue;
        var card =
          root.querySelector('[data-st11-id="' + pid + '"]') ||
          root.querySelector('[data-product-id="' + pid + '"]');
        if (isTannerDomCard(card)) return true;
      }
      return false;
    }

    function hideDuplicateCards(cart) {
      var parentBlockedVariants = {};
      var parentBlockedProductIds = {};
      var cartBlockedVariants = {};
      var cartBlockedProductIds = {};
      var tannerBlocked = parentSelectionHasTanner();
      var upsellFreeGiftAtLimit = countLpPpUpsellFreeLines(cart) >= 1;

      (cart && cart.items ? cart.items : []).forEach(function (item) {
        var vid = Number(item.variant_id);
        var pid = Number(item.product_id);
        if (vid) cartBlockedVariants[vid] = true;
        if (pid) cartBlockedProductIds[pid] = true;
        if (isTannerCartItem(item)) {
          tannerBlocked = true;
          if (pid) parentBlockedProductIds[pid] = true;
        }
      });

      (getParentVariantIds() || []).forEach(function (id) {
        parentBlockedVariants[Number(id)] = true;
      });

      (getParentProductIds() || []).forEach(function (id) {
        var pid = Number(id);
        if (pid) parentBlockedProductIds[pid] = true;
      });

      allModalCards().forEach(function (card) {
        var mode = card.getAttribute("data-st11-mode") || "";
        var vid = Number(card.getAttribute("data-st11-variant"));
        var pid = Number(card.getAttribute("data-product-id") || card.getAttribute("data-st11-id"));
        var hide = false;

        if (mode === "gift") {
          if (upsellFreeGiftAtLimit) {
            hide = true;
          } else if (cartBlockedVariants[vid] || parentBlockedVariants[vid]) {
            hide = true;
          } else if (
            (pid && cartBlockedProductIds[pid]) ||
            (pid && parentBlockedProductIds[pid])
          ) {
            hide = true;
          }
        } else if (mode === "upsell") {
          // Upsell step: dedupe BOGO parent picks + tanner catalog only.
          // Do not hide every upsell whose variant happens to be elsewhere in cart.
          if (parentBlockedVariants[vid]) {
            hide = true;
          } else if (pid && parentBlockedProductIds[pid]) {
            hide = true;
          } else if (tannerBlocked && isTannerDomCard(card)) {
            hide = true;
          }
        }

        if (hide) {
          card.hidden = true;
          card.classList.remove("is-selected");
        } else if (card.getAttribute("data-st11-available") === "true") {
          card.hidden = false;
        }
      });
      reindexSlots();
      syncUpsellEmptyState();
    }

    function syncUpsellEmptyState() {
      if (!step1Panel || !enableUpsellStep) return;
      var visible = visibleModalCards("upsell").length;
      var emptyEl = step1Panel.querySelector(".st11-modal__empty");
      if (visible === 0) {
        if (!emptyEl) {
          emptyEl = document.createElement("p");
          emptyEl.className = "lp-text lp-text--sm st11-modal__empty";
          emptyEl.textContent = "No eligible add-on items.";
          var grid = step1Panel.querySelector("[data-st11-upsell-grid]");
          if (grid && grid.parentElement) {
            grid.parentElement.insertBefore(emptyEl, grid.nextSibling);
          } else {
            step1Panel.appendChild(emptyEl);
          }
        }
        emptyEl.hidden = false;
      } else if (emptyEl) {
        emptyEl.hidden = true;
      }
    }

    function premintVisibleModalOffers() {
      var offers = [];
      ["upsell", "gift"].forEach(function (mode) {
        visibleModalCards(mode).forEach(function (card) {
          var d = cardData(card);
          offers.push({ mode: mode, id: d.id, variant: d.variant, pct: upsellOfferPct });
        });
      });
      premintLpOffers(offers);
    }

    function selectedInMode(mode) {
      var scope = modal || root;
      var el = scope.querySelector(
        '[data-st11-card][data-st11-mode="' + mode + '"].is-selected'
      );
      return el ? cardData(el) : null;
    }

    function selectedAllInMode(mode) {
      var scope = modal || root;
      return Array.prototype.slice
        .call(
          scope.querySelectorAll(
            '[data-st11-card][data-st11-mode="' + mode + '"].is-selected'
          )
        )
        .map(cardData);
    }

    function hasUpsellSelected() {
      return !!selectedInMode("upsell");
    }

    function canIncludeGift() {
      if (!enableFreeGift) return false;
      if (!enableUpsellStep) return true;
      return hasUpsellSelected();
    }

    function clearGiftSelection() {
      modalCards("gift").forEach(function (c) {
        c.classList.remove("is-selected");
        var btn = c.querySelector("[data-st11-toggle]");
        var txt = c.querySelector("[data-st11-btn-text]");
        var giftCheck = c.querySelector("[data-st11-btn-check]");
        if (btn) {
          btn.setAttribute("aria-pressed", "false");
          var base = btn.getAttribute("data-st11-base-label") || "";
          if (txt) txt.textContent = base;
        }
        if (giftCheck) giftCheck.hidden = true;
      });
    }

    function notifyUpsellRequiredForGift() {
      if (onCheckoutBlocked) onCheckoutBlocked({ reason: "upsell_required_for_gift" });
      emit("checkoutBlocked", { reason: "upsell_required_for_gift" });
    }

    function notifyFreeGiftLimitReached() {
      if (onCheckoutBlocked) onCheckoutBlocked({ reason: "free_gift_limit_reached" });
      emit("checkoutBlocked", { reason: "free_gift_limit_reached" });
    }

    function lpUpsellFreeGiftAtLimit(cart) {
      return countLpPpUpsellFreeLines(cart || getCartSnapshot()) >= 1;
    }

    function securedLabelFromCard(card) {
      if (!card) return "";
      var title = card.getAttribute("data-st11-title") || "";
      var sel = card.querySelector("[data-st11-variant-select]");
      var variantLabel = "";
      if (sel && sel.options.length) {
        var opt = sel.options[sel.selectedIndex];
        variantLabel = (opt.getAttribute("data-variant-label") || opt.textContent || "")
          .replace(/\s*-\s*[^-]+$/, "")
          .replace(/\s*—\s*Sold out\s*$/, "")
          .trim();
        if (variantLabel === "Default Title") variantLabel = "";
      }
      if (variantLabel) {
        return title + " (" + variantLabel + ")";
      }
      return title;
    }

    function syncVariantInTitle(card) {
      if (!card || card.getAttribute("data-st11-mode") !== "upsell") return;
      var titleEl = card.querySelector("[data-st11-variant-in-title]");
      if (!titleEl) return;
      titleEl.textContent = "";
      titleEl.hidden = true;
    }

    function initialModalStep() {
      if (enableUpsellStep) return 1;
      if (enableFreeGift) return 2;
      return 1;
    }

    function goToStep(n) {
      if (!enableUpsellStep && enableFreeGift && n === 1) n = 2;
      if (!enableFreeGift && n > 1) n = 1;
      if (!enableUpsellStep && !enableFreeGift) n = 1;
      if (n === 2 && enableUpsellStep && enableFreeGift && !hasUpsellSelected()) {
        notifyUpsellRequiredForGift();
        n = 1;
      }
      modalStep = n;
      if (step1Panel && enableUpsellStep) {
        step1Panel.classList.toggle("is-active", n === 1);
        step1Panel.hidden = n !== 1;
      }
      if (step2Panel && enableFreeGift) {
        step2Panel.classList.toggle("is-active", n === 2);
        step2Panel.hidden = n !== 2;
        step2Panel.classList.toggle("is-locked", n < 2);
        step2Panel.setAttribute("aria-disabled", n < 2 ? "true" : "false");
      }
      if (giftStep && enableFreeGift) {
        giftStep.classList.toggle("is-locked", n < 2);
        giftStep.setAttribute("aria-disabled", n < 2 ? "true" : "false");
      }
      syncProgress();
      syncBackButton();
    }

    function syncBackButton() {
      if (!modalBackBtn) return;
      var show = enableUpsellStep && enableFreeGift && modalStep === 2;
      modalBackBtn.hidden = !show;
      if (modalBackBar) modalBackBar.hidden = !show;
    }

    function syncProgress() {
      var hasUpsell = !!selectedInMode("upsell");
      var hasGift = !!selectedInMode("gift");

      if (progressSteps.length) {
        progressSteps.forEach(function (el) {
          var stepNum = el.getAttribute("data-st11-progress-step");
          if (!enableUpsellStep && stepNum === "1") return;
          if (!enableFreeGift && !enableUpsellStep && stepNum === "2") return;
          var isStep1 = stepNum === "1";
          var isStep2 = stepNum === "2";
          var step2Active = enableFreeGift
            ? isStep2 && modalStep === 2 && !hasGift
            : isStep2 && hasUpsell;
          el.classList.toggle(
            "is-active",
            (isStep1 && modalStep === 1 && !hasUpsell) || step2Active
          );
          el.classList.toggle(
            "is-complete",
            (isStep1 && hasUpsell) || (isStep2 && hasGift)
          );
        });
      }

      if (progressLine) {
        progressLine.classList.remove("is-half", "is-full");
        if (enableUpsellStep && enableFreeGift) {
          if (hasGift) {
            progressLine.classList.add("is-full");
          } else if (hasUpsell || modalStep === 2) {
            progressLine.classList.add("is-half");
          }
        } else if (enableUpsellStep && !enableFreeGift && hasUpsell) {
          progressLine.classList.add("is-half");
        }
      }

      if (step1Label && step1Done) {
        step1Label.hidden = hasUpsell;
        step1Done.hidden = !hasUpsell;
      }

      if (securedBanner && securedName && enableFreeGift && enableUpsellStep) {
        var upsellEl = (modal || root).querySelector(
          '[data-st11-card][data-st11-mode="upsell"].is-selected'
        );
        if (hasUpsell && modalStep === 2) {
          securedName.textContent = securedLabelFromCard(upsellEl);
          securedBanner.hidden = false;
        } else {
          securedBanner.hidden = true;
          securedName.textContent = "";
        }
      }

      if (checkoutBtn) {
        var checkoutText = checkoutBtn.querySelector(".st11-modal__checkout-text");
        if (!enableFreeGift || (!enableUpsellStep && enableFreeGift)) {
          checkoutBtn.disabled = false;
          if (checkoutText) checkoutText.textContent = "Secure Checkout";
        } else if (modalStep === 1) {
          checkoutBtn.disabled = !hasUpsell;
          if (checkoutText) {
            checkoutText.textContent = hasUpsell ? "Continue" : "Select an add-on to continue";
          }
        } else {
          checkoutBtn.disabled = false;
          if (checkoutText) checkoutText.textContent = "Secure Checkout";
        }
      }
    }

    // No card is pre-selected. The modal opens with nothing chosen so every add
    // is a deliberate click: under multi-pick a pre-selected card does not get
    // swapped out by the next click, it gets added alongside it, so a shopper
    // reaching for a second add-on would have bought an add-on they never chose.
    // The single-pick flow already gates on this ("Select an add-on to continue"),
    // so both flows now open the same honest way.

    function resetModalFlow() {
      modalStep = initialModalStep();
      allModalCards().forEach(function (c) {
        c.classList.remove("is-selected");
        var btn = c.querySelector("[data-st11-toggle]");
        var txt = c.querySelector("[data-st11-btn-text]");
        var giftCheck = c.querySelector("[data-st11-btn-check]");
        if (btn) {
          btn.setAttribute("aria-pressed", "false");
          var base = btn.getAttribute("data-st11-base-label") || "";
          if (txt) txt.textContent = base;
        }
        if (giftCheck) giftCheck.hidden = true;
      });
      goToStep(modalStep);
    }

    function singleSelect(card, mode, advance) {
      if (card.getAttribute("data-st11-available") !== "true") return;
      if (mode === "gift") {
        if (!enableFreeGift || (enableUpsellStep && modalStep < 2)) return;
        if (enableUpsellStep && !hasUpsellSelected()) {
          notifyUpsellRequiredForGift();
          return;
        }
      }
      // Narrowing the group to the clicked card is the whole of multi-pick:
      // the loop below only ever deselects siblings because they are IN the
      // group, so leaving them out keeps every earlier pick lit.
      var group = mode === "upsell" && allowMultiUpsell ? [card] : modalCards(mode);
      var willSelect = !card.classList.contains("is-selected");
      if (mode === "gift" && willSelect && lpUpsellFreeGiftAtLimit()) {
        notifyFreeGiftLimitReached();
        return;
      }
      group.forEach(function (c) {
        var on = c === card && willSelect;
        c.classList.toggle("is-selected", on);
        var btn = c.querySelector("[data-st11-toggle]");
        var txt = c.querySelector("[data-st11-btn-text]");
        if (btn) btn.setAttribute("aria-pressed", on ? "true" : "false");
        if (txt) {
          var base = btn ? btn.getAttribute("data-st11-base-label") : "";
          // The glyph is the colour-independent half of the selected cue; the
          // accent fill is the other half. aria-pressed above carries it for AT.
          txt.textContent = on ? "\u2713 Selected" : base;
        }
        var giftCheck = c.querySelector("[data-st11-btn-check]");
        if (giftCheck && mode === "gift") {
          giftCheck.hidden = !on;
        }
      });
      if (mode === "upsell" && !willSelect && enableFreeGift) {
        clearGiftSelection();
      }
      if (enableUpsellStep && enableFreeGift && mode === "upsell" && willSelect && advance !== false) {
        goToStep(2);
      }
      syncProgress();
      emit("singleSelect", { mode: mode, card: cardData(card), selected: willSelect });
    }

    function focusables() {
      if (!modal) return [];
      return Array.prototype.slice
        .call(
          modal.querySelectorAll(
            'a[href], button:not([disabled]), select, input, [tabindex]:not([tabindex="-1"])'
          )
        )
        .filter(function (el) {
          return el.offsetParent !== null;
        });
    }

    function closeModal() {
      if (!modal || modal.dataset.open !== "1") return;
      modal.dataset.open = "0";
      modal.hidden = true;
      modal.setAttribute("hidden", "");
      document.documentElement.style.overflow = "";
      stickySuppress(false);
      if (lastFocused && typeof lastFocused.focus === "function") {
        lastFocused.focus();
      }
    }

    function modalCheckoutIntent(intent) {
      var giftAllowed =
        canIncludeGift() && !!selectedInMode("gift") && !lpUpsellFreeGiftAtLimit();
      if (intent === "checkout") {
        return { includeUpsell: enableUpsellStep, includeGift: giftAllowed };
      }
      if (!enableFreeGift || (enableUpsellStep && modalStep === 1)) {
        return { includeUpsell: false, includeGift: false };
      }
      return { includeUpsell: true, includeGift: false };
    }

    // Dual-write `_bbe_offer` (signed token, when premint cache is warm) +
    // `_upsell_qty` qty-lock fallback on top of the legacy `_is_lp_upsells*`
    // props. Never blocks ATC — missing token means legacy props only.
    // Gift lines skip `_upsell_qty` (gift markers hard-cap at 1 in the
    // upsell-line-validation Function, matching the cart-drawer freebie).
    function offerProps(props, mode, d) {
      if (mode !== "gift") props._upsell_qty = "1";
      try {
        var token = readOfferMintCache(
          offerMintCacheKey(lpOfferIdForCard(mode, d), d.variant, 1)
        );
        if (token) props._bbe_offer = token;
      } catch (e) {
        /* legacy props only */
      }
      return props;
    }

    function buildCartItems(opts) {
      opts = opts || {};
      var items = (getParentCartItems() || []).slice();
      var upsells = selectedAllInMode("upsell");
      var gift = selectedInMode("gift");
      var freeGiftAtLimit = lpUpsellFreeGiftAtLimit();

      if (enableUpsellStep && opts.includeUpsell !== false) {
        upsells.forEach(function (upsell) {
          items.push({
            id: Number(upsell.variant),
            quantity: 1,
            properties: offerProps(trackProps("upsell", upsell.slot), "upsell", upsell),
          });
        });
      }
      if (
        enableFreeGift &&
        opts.includeGift !== false &&
        gift &&
        canIncludeGift() &&
        !freeGiftAtLimit
      ) {
        items.push({
          id: Number(gift.variant),
          quantity: 1,
          properties: offerProps(trackProps("gift", gift.slot), "gift", gift),
        });
      }
      return items;
    }

    function applyParentDiscountThenCheckout() {
      var go = function () {
        window.location.href = checkoutUrl;
      };
      if (!discountCode) {
        go();
        return;
      }
      if (window.liquidAjaxCart && typeof window.liquidAjaxCart.update === "function") {
        window.liquidAjaxCart.update({ discount: discountCode }, { lastCallback: go });
        return;
      }
      fetch((window.routes && window.routes.cart_update_url) || "/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ discount: discountCode }),
      })
        .catch(function () {})
        .finally(go);
    }

    function pushUpsellAnalytics(opts) {
      var added = [];
      if (opts.includeUpsell !== false) {
        selectedAllInMode("upsell").forEach(function (upsell) {
          added.push({
            item_id: upsell.id,
            item_variant_id: upsell.variant,
            item_name: upsell.title,
            bbe_track_source: "lp_upsells",
            bbe_track_event: "add",
            bbe_track_slot: upsell.slot,
            bbe_upsell_lp: true,
          });
        });
      }
      if (enableFreeGift && opts.includeGift !== false) {
        var gift = selectedInMode("gift");
        if (gift) {
          added.push({
            item_id: gift.id,
            item_variant_id: gift.variant,
            item_name: gift.title,
            bbe_track_source: "lp_upsells",
            bbe_track_event: "add",
            bbe_track_slot: gift.slot,
            bbe_upsell_lp_gift: true,
          });
        }
      }
      if (!added.length) return;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "bbe_upsell_add",
        surface: "lp_upsells",
        bbe_track_source: "lp_upsells",
        items: added,
      });

      if (typeof window.recordCartStat === "function") {
        // PPUP's add-attempt stat, so popup adds land in one bucket.
        // `normalizeViewStatType` maps it to `prePurchasePopupUpsellAdd`.
        window.recordCartStat("__cartUpsellAddPreupsell", { dedupe: false });
      }
    }

    function checkout(btn, opts, intent) {
      opts = opts || {};
      intent = intent || "checkout";
      if (checkoutBusy) return;

      if (!canCheckout(opts)) {
        if (onCheckoutBlocked) onCheckoutBlocked(opts);
        emit("checkoutBlocked", { intent: intent, opts: opts });
        return;
      }

      var items = buildCartItems(opts);
      if (!items.length) {
        emit("checkoutBlocked", { intent: intent, opts: opts, reason: "empty" });
        return;
      }

      checkoutBusy = true;
      setLoading(btn, true);

      fetch(cartAddUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error("cart");
          return res.json();
        })
        .then(function () {
          pushUpsellAnalytics(opts);
          emit(intent === "dismiss" ? "dismiss" : intent === "skip" ? "skip" : "checkout", {
            opts: opts,
            items: items,
          });
          applyParentDiscountThenCheckout();
        })
        .catch(function () {
          checkoutBusy = false;
          setLoading(btn, false);
        });
    }

    function openModal() {
      if (!modal) return;
      if (modal.dataset.open === "1" && !modal.hidden) return;
      if (modal.dataset.open === "1" && modal.hidden) {
        delete modal.dataset.open;
      }

      fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (res) {
          return res.ok ? res.json() : { items: [] };
        })
        .catch(function () {
          return { items: [] };
        })
        .then(function (cart) {
          hideDuplicateCards(cart);
          premintVisibleModalOffers();
          // Popup impression. Adds report on the PPUP surface, so views must
          // too — otherwise LP adds inflate the popup's numerator with no
          // matching denominator and overstate its conversion rate. Deduped
          // per session, matching pre-upsell.js's single impression per open.
          if (typeof window.recordCartStat === "function") {
            window.recordCartStat("__cartPreupsell", { dedupe: true });
          }
          lastFocused = document.activeElement;
          resetModalFlow();

          modal.hidden = false;
          modal.removeAttribute("hidden");
          modal.dataset.open = "1";
          document.documentElement.style.overflow = "hidden";
          stickySuppress(true);

          if (modalCloseBtn) modalCloseBtn.focus();
          else {
            var f = focusables();
            if (f.length) f[0].focus();
          }

          emit("open", {
            enableUpsellStep: enableUpsellStep,
            enableFreeGift: enableFreeGift,
            modalStep: modalStep,
          });
        });
    }

    function onModalKeydown(e) {
      if (!modal || modal.dataset.open !== "1") return;
      if (e.key === "Escape") {
        e.preventDefault();
        checkout(checkoutBtn || modalCloseBtn, modalCheckoutIntent("dismiss"), "dismiss");
        return;
      }
      if (e.key === "Tab") {
        var f = focusables();
        if (!f.length) return;
        var first = f[0];
        var last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function applyVariantSelect(sel) {
      var card = sel.closest("[data-st11-card]");
      if (!card) return;
      var opt = sel.options[sel.selectedIndex];
      var price = parseInt(opt.getAttribute("data-price"), 10) || 0;
      var compare = parseInt(opt.getAttribute("data-compare"), 10) || 0;
      card.setAttribute("data-st11-variant", opt.value);
      card.setAttribute("data-st11-price", String(price));
      card.setAttribute("data-st11-compare", String(compare));
      var nowEl = card.querySelector("[data-st11-price-display]");
      var compareEl = card.querySelector("[data-st11-compare-display]");
      var isUpsell = card.getAttribute("data-st11-mode") === "upsell";
      // Per-card pct is authoritative and may legitimately be 0 (product not in
      // the offer collection, or no live offer) — never fall back to a default.
      var pct = parseInt(card.getAttribute("data-st11-offer-pct"), 10);
      if (!isFinite(pct) || pct < 1 || pct > 90) pct = 0;
      var isDiscounted = isUpsell && pct > 0;
      if (nowEl) {
        // Half-up, matching the Liquid card and the transform's rounding.
        nowEl.textContent = isDiscounted
          ? money(Math.floor((price * (100 - pct) + 50) / 100))
          : money(price);
      }
      if (compareEl) {
        // Mirrors the Liquid card: strike the explicit compare-at when one is
        // set, otherwise the undiscounted listing price. Hidden only when
        // there is nothing genuine to strike against.
        var strike = compare > price ? compare : price;
        var showStrike = isDiscounted || compare > price;
        compareEl.textContent = showStrike ? money(strike) : "";
        compareEl.hidden = !showStrike;
      }
      syncVariantInTitle(card);
      if (card.classList.contains("is-selected")) {
        syncProgress();
      }
      var d = cardData(card);
      premintLpOffers([
        { mode: d.mode === "gift" ? "gift" : "upsell", id: d.id, variant: d.variant, pct: upsellOfferPct },
      ]);
      emit("variantSelect", { card: d });
    }

    function onModalClick(e) {
      if (!modal || modal.dataset.open !== "1" || !modal.contains(e.target)) return;

      var toggle = e.target.closest("[data-st11-toggle]");
      if (toggle) {
        var card = toggle.closest("[data-st11-card]");
        if (!card) return;
        var mode = card.getAttribute("data-st11-mode");
        if (mode === "upsell" || mode === "gift") {
          singleSelect(card, mode);
        }
        return;
      }

      var modalCard = e.target.closest(
        '[data-st11-card][data-st11-mode="upsell"], [data-st11-card][data-st11-mode="gift"]'
      );
      if (modalCard) {
        if (e.target.closest("a, select, button, [data-st11-variant-select]")) return;
        singleSelect(modalCard, modalCard.getAttribute("data-st11-mode"));
        return;
      }

      if (e.target.closest("[data-st11-checkout]")) {
        if (enableUpsellStep && enableFreeGift && modalStep === 1) {
          if (!hasUpsellSelected()) {
            notifyUpsellRequiredForGift();
            return;
          }
          goToStep(2);
          syncProgress();
          return;
        }
        checkout(
          e.target.closest("[data-st11-checkout]"),
          modalCheckoutIntent("checkout"),
          "checkout"
        );
        return;
      }

      if (e.target.closest("[data-st11-skip]")) {
        checkout(e.target.closest("[data-st11-skip]"), modalCheckoutIntent("skip"), "skip");
        return;
      }

      if (e.target.closest("[data-st11-change-item]")) {
        e.preventDefault();
        resetModalFlow();
        emit("changeItem", {});
        return;
      }

      if (e.target.closest("[data-st11-modal-back]")) {
        e.preventDefault();
        if (enableUpsellStep && enableFreeGift && modalStep === 2) {
          goToStep(1);
          if (modalBackBtn) modalBackBtn.blur();
        }
        return;
      }

      if (e.target.closest("[data-st11-modal-close]")) {
        checkout(
          e.target.closest("[data-st11-modal-close]"),
          modalCheckoutIntent("dismiss"),
          "dismiss"
        );
        return;
      }
    }

    function onModalChange(e) {
      if (!modal || !modal.contains(e.target)) return;
      var sel = e.target.closest("[data-st11-variant-select]");
      if (!sel) return;
      applyVariantSelect(sel);
    }

    if (modal) {
      modal.addEventListener("click", onModalClick);
      modal.addEventListener("change", onModalChange);
      document.addEventListener("keydown", onModalKeydown);
    }

    hideOosModalCards();
    applyStepUi();
    modalCards("upsell").forEach(syncVariantInTitle);
    reindexSlots();

    var api = {
      open: openModal,
      close: closeModal,
      reset: resetModalFlow,
      buildCartItems: buildCartItems,
      applyParentDiscountThenCheckout: applyParentDiscountThenCheckout,
    };

    lastInstance = api;
    window.BBELP.modal.open = openModal;

    return api;
  }

  window.BBELP.modal = {
    init: init,
    open: function () {
      if (lastInstance && typeof lastInstance.open === "function") {
        lastInstance.open();
      }
    },
  };
})();
